import { createFileRoute, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import {
 getLandingPagesDirectory,
 getLandingPagesTotal,
 LANDING_PAGES_PER_DIRECTORY_PAGE,
} from "@/lib/seo-pages.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/landing-pages/directory/$page")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const page = parseInt(params.page, 10);
 if (!Number.isFinite(page) || page < 0) throw notFound();
 const [{ total }, { pages }] = await Promise.all([
 getLandingPagesTotal(),
 getLandingPagesDirectory({ data: { page } }),
 ]);
 if (pages.length === 0) throw notFound();
 return { page, total, pages };
 },
 head: ({ params, loaderData }) => {
 const page = loaderData?.page ?? params.page;
 const title = `Landing Pages, Page ${page} | Heallexa`;
 const description = `Browse Heallexa landing pages, page ${page}. Specialty + location + insurance combinations across the United States.`;
 const url = `${SITE}/landing-pages/directory/${page}`;
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:url", content: url },
 ],
 links: [{ rel: "canonical", href: url }],
 };
 },
 component: LandingDirectoryPage,
});

function LandingDirectoryPage() {
 const { page, total, pages } = Route.useLoaderData();
 const totalPages = Math.ceil(total / LANDING_PAGES_PER_DIRECTORY_PAGE);
 const last = Math.max(totalPages - 1, 0);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-6xl px-6 py-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-primary">Sitemap</a>
 <span className="mx-2">/</span>
 <a href="/specialties" className="hover:text-primary">Specialties</a>
 <span className="mx-2">/</span>
 <a href="/locations" className="hover:text-primary">Locations</a>
 <span className="mx-2">/</span>
 <a href="/insurance" className="hover:text-primary">Insurance</a>
 <span className="mx-2">/</span>
 <span className="text-foreground">Page {page}</span>
 </nav>

 <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-2">
 Landing Pages, Page {page}
 </h1>
 <p className="text-sm text-muted-foreground mb-8">
 {pages.length.toLocaleString()} of {total.toLocaleString()} pages, grouped by category.
 </p>

 {Object.entries(groupByCategory(pages)).map(([category, items]) => (
 <section key={category} className="mb-10">
 <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
 {category} <span className="text-muted-foreground/70 font-normal">({items.length})</span>
 </h2>
 <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-6">
 {items.map((p: any) => (
 <li key={p.slug}>
 <a
 href={p.slug}
 className="text-sm text-muted-foreground hover:text-primary transition truncate block"
 >
 {p.title ?? p.slug}
 </a>
 </li>
 ))}
 </ul>
 </section>
 ))}

 <nav className="mt-12 flex items-center justify-between text-sm">
 <a
 href={page > 0 ? `/landing-pages/directory/${page - 1}` : "/landing-pages/directory"}
 className="px-3 py-1.5 rounded-md bg-card border border-border hover:bg-accent"
 >
 ← Previous
 </a>
 <span className="text-muted-foreground">
 Page {page} of {last}
 </span>
 <a
 href={page < last ? `/landing-pages/directory/${page + 1}` : `/landing-pages/directory/${page}`}
 className="px-3 py-1.5 rounded-md bg-card border border-border hover:bg-accent"
 >
 Next →
 </a>
 </nav>
 </main>
 <FooterSection />
 </div>
 );
}

const CATEGORY_LABELS: Record<string, string> = {
 specialty_city: "Specialty + Location",
 specialty_state: "Specialty + State",
 specialty_insurance: "Specialty + Insurance",
 specialty_insurance_city: "Specialty + Insurance + Location",
 insurance_city: "Insurance + Location",
 condition: "Conditions",
 service: "Services",
};

function groupByCategory(items: any[]): Record<string, any[]> {
 const grouped: Record<string, any[]> = {};
 for (const item of items) {
 const key = CATEGORY_LABELS[item.page_type] ?? "Other";
 (grouped[key] ||= []).push(item);
 }
 return Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)));
}
